/**
 * useDefaultStatusFilter — Generic hook for applying a default status filter
 * on the main table at the start of each browser session.
 *
 * Session behavior:
 * - New session (login / new tab): applies the default status filter
 * - User removes the filter during session: honored, stays removed
 * - Next login: re-applied automatically
 *
 * If no matching data exists, the filter is automatically cleared.
 *
 * Uses useLayoutEffect for the initial filter application to prevent a
 * visual flicker — the default is applied before the browser paints, so
 * the first visible render already has the correct filters.
 */

import {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

export function useDefaultStatusFilter<F>({
  sessionKey,
  applyFilter,
  clearFilter,
  setFilters,
  isFilteredEmpty,
  dataStatus,
  enabled,
}: {
  /** Unique sessionStorage key for this table (e.g., 'seed-default-active-filter') */
  sessionKey: string;
  /** Given previous filters, return new filters with the default status applied */
  applyFilter: (prev: F) => F;
  /** Given previous filters, return new filters with the status cleared */
  clearFilter: (prev: F) => F;
  /** Filter state setter */
  setFilters: Dispatch<SetStateAction<F>>;
  /** Whether the filtered query returned zero results */
  isFilteredEmpty: boolean;
  /** Query loading status */
  dataStatus: 'pending' | 'error' | 'success';
  /** Set to false for drawer/other contexts to disable entirely */
  enabled: boolean;
}) {
  const appliedDefaultRef = useRef(false);

  // useLayoutEffect ensures the default filter is applied before the browser
  // paints, preventing a visual flicker where old localStorage filters briefly
  // appear before being overridden by session defaults.
  useLayoutEffect(() => {
    if (!enabled) return;

    let alreadyInitialized = false;
    try {
      alreadyInitialized = !!sessionStorage.getItem(sessionKey);
    } catch {
      // sessionStorage unavailable (e.g., Safari private browsing)
    }
    if (alreadyInitialized) return;

    setFilters(applyFilter);
    appliedDefaultRef.current = true;

    try {
      sessionStorage.setItem(sessionKey, 'true');
    } catch {
      // sessionStorage unavailable
    }
  }, [enabled, setFilters, sessionKey, applyFilter]);

  useEffect(() => {
    if (!enabled || !appliedDefaultRef.current) return;
    if (dataStatus !== 'success') return;

    if (isFilteredEmpty) {
      setFilters(clearFilter);
    }

    appliedDefaultRef.current = false;
  }, [enabled, dataStatus, isFilteredEmpty, setFilters, clearFilter]);
}
